import { useEffect, useRef, useMemo } from 'react';
import { Chart } from 'chart.js/auto';

const METRIC_COLORS = {
  registeredUsers: '#102734',
  kycApproved: '#d4b06f',
  pendingKyc: '#18384a',
  flaggedForReview: '#dc3545',
  totalTransactions: '#bf9a52',
  reconciliationIssues: '#28a745',
};

const buildChartPayload = (metrics, metricDefs, colorMap) => {
  const labels = [];
  const values = [];
  const colors = [];
  metricDefs.forEach((metric) => {
    labels.push(metric.label);
    values.push(Number(metrics?.[metric.key]) || 0);
    colors.push(colorMap[metric.key] || '#102734');
  });
  return { labels, values, colors };
};

const PlatformActivityChart = ({
  metrics,
  metricDefs,
  chartType,
  onChartTypeChange,
  title = 'Visual breakdown',
  emptyMessage = 'No activity data to chart yet.',
  ariaLabel = 'Platform activity breakdown',
  colorMap = METRIC_COLORS,
}) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const chartData = useMemo(
    () => buildChartPayload(metrics, metricDefs, colorMap),
    [metrics, metricDefs, colorMap]
  );
  const total = chartData.values.reduce((sum, value) => sum + value, 0);

  useEffect(() => {
    if (!canvasRef.current) return undefined;

    chartRef.current?.destroy();
    const ctx = canvasRef.current.getContext('2d');
    const isPie = chartType === 'pie';

    chartRef.current = new Chart(ctx, {
      type: isPie ? 'pie' : 'bar',
      data: {
        labels: chartData.labels,
        datasets: [{
          label: 'Count',
          data: chartData.values,
          backgroundColor: chartData.colors,
          borderColor: isPie ? '#ffffff' : chartData.colors,
          borderWidth: isPie ? 2 : 0,
          borderRadius: isPie ? 0 : 6,
          hoverOffset: isPie ? 6 : 0,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: isPie,
            position: 'bottom',
            labels: {
              boxWidth: 10,
              boxHeight: 10,
              padding: 12,
              font: { family: "'DM Sans', 'Inter', sans-serif", size: 11 },
              color: '#64748b',
            },
          },
          tooltip: {
            backgroundColor: '#0b1f2a',
            titleFont: { family: "'DM Sans', 'Inter', sans-serif", size: 12 },
            bodyFont: { family: "'DM Sans', 'Inter', sans-serif", size: 12 },
            padding: 10,
            callbacks: {
              label: (ctx) => {
                const value = Number(ctx.parsed.y ?? ctx.parsed) || 0;
                if (!isPie || total <= 0) return ` ${value.toLocaleString()}`;
                const pct = Math.round((value / total) * 100);
                return ` ${value.toLocaleString()} (${pct}%)`;
              },
            },
          },
        },
        scales: isPie ? {} : {
          x: {
            grid: { display: false },
            ticks: {
              maxRotation: 45,
              minRotation: 45,
              font: { family: "'DM Sans', 'Inter', sans-serif", size: 10 },
              color: '#64748b',
            },
          },
          y: {
            beginAtZero: true,
            ticks: {
              precision: 0,
              font: { family: "'DM Sans', 'Inter', sans-serif", size: 11 },
              color: '#64748b',
            },
            grid: { color: 'rgba(16, 39, 52, 0.08)' },
          },
        },
      },
    });

    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, [chartData, chartType, total]);

  return (
    <div className="platform-activity-chart-panel">
      <div className="platform-activity-chart-head">
        <span className="platform-activity-chart-title">{title}</span>
        <div className="platform-activity-chart-toggle" role="group" aria-label="Chart type">
          <button
            type="button"
            className={`filter-btn${chartType === 'pie' ? ' active' : ''}`}
            onClick={() => onChartTypeChange('pie')}
            aria-pressed={chartType === 'pie'}
          >
            <i className="fas fa-chart-pie" aria-hidden="true"></i> Pie
          </button>
          <button
            type="button"
            className={`filter-btn${chartType === 'bar' ? ' active' : ''}`}
            onClick={() => onChartTypeChange('bar')}
            aria-pressed={chartType === 'bar'}
          >
            <i className="fas fa-chart-bar" aria-hidden="true"></i> Histogram
          </button>
        </div>
      </div>
      <div className="platform-activity-chart-canvas-wrap">
        {total === 0 ? (
          <p className="platform-activity-chart-empty">{emptyMessage}</p>
        ) : (
          <canvas ref={canvasRef} aria-label={ariaLabel} />
        )}
      </div>
    </div>
  );
};

export default PlatformActivityChart;
